// hooks/useAnalytics.ts
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getAnalyticsId, isAnalyticsEnabled } from '../utils/analytics-config';

// Define gtag function for tracking
declare global {
  interface Window {
    gtag?: (command: string, action: string, parameters: Record<string, unknown>) => void;
    dataLayer?: any[];
  }
}

// Initialize Google Analytics dynamically
const initializeGoogleAnalytics = () => {
  const GA_MEASUREMENT_ID = getAnalyticsId();
  
  if (!GA_MEASUREMENT_ID || !isAnalyticsEnabled() || typeof window === 'undefined') {
    console.warn('Google Analytics not initialized - ID not configured');
    return;
  }

  // Load gtag script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function(...args: any[]) {
    window.dataLayer?.push(args);
  };
  
  // Initialize Google Analytics
  (window.gtag as any)('js', new Date());
  (window.gtag as any)('config', GA_MEASUREMENT_ID, {
    page_title: document.title,
    page_location: window.location.href,
  });
  
};

export const useAnalytics = () => {
  const location = useLocation();

  // Initialize Google Analytics on first load
  useEffect(() => {
    if (!window.gtag && isAnalyticsEnabled()) {
      initializeGoogleAnalytics();
    }
  }, []);

  // Track page views
  useEffect(() => {
    const GA_MEASUREMENT_ID = getAnalyticsId();
    if (window.gtag && GA_MEASUREMENT_ID) {
      window.gtag('config', GA_MEASUREMENT_ID, {
        page_path: location.pathname + location.search,
        page_title: document.title,
        page_location: window.location.href,
      });
    }
  }, [location]);

  // Custom event tracking functions
  const trackEvent = (action: string, category: string, label?: string, value?: number) => {
    if (window.gtag) {
      window.gtag('event', action, {
        event_category: category,
        event_label: label,
        value: value,
      });
    }
  };

  // Content-specific tracking functions
  const trackContentView = (contentType: 'cinema' | 'content' | 'podcast' | 'animation' | 'sneak-peek' | 'live-video', contentId: string, contentTitle: string) => {
    trackEvent('view_content', `${contentType}_view`, `${contentTitle} (${contentId})`);
  };

  const trackContentPlay = (contentType: 'cinema' | 'podcast' | 'animation' | 'live-video', contentId: string, contentTitle: string) => {
    trackEvent('play_content', `${contentType}_play`, `${contentTitle} (${contentId})`);
  };

  const trackUserInteraction = (action: string, element: string) => {
    trackEvent(action, 'user_interaction', element);
  };

  const trackSearch = (searchTerm: string, resultsCount?: number) => {
    trackEvent('search', 'content_search', searchTerm, resultsCount);
  };

  const trackShare = (contentType: string, contentId: string, platform: string) => {
    trackEvent('share', `${contentType}_share`, `${platform}: ${contentId}`);
  };

  return {
    trackEvent,
    trackContentView,
    trackContentPlay,
    trackUserInteraction,
    trackSearch,
    trackShare,
  };
};

// Utility function for manual initialization (optional)
export const initializeAnalytics = () => {
  initializeGoogleAnalytics();
};
